import React, { useRef } from 'react';
import moment from 'moment';
import {
  Chart as ChartJS,
  PointElement,
  LineElement,
  Legend,
  Tooltip,
  Title,
  TimeScale,
  TimeSeriesScale,
} from 'chart.js';
import { getElementAtEvent, Line } from 'react-chartjs-2';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import 'chartjs-adapter-moment';
import { isMobile } from 'react-device-detect';

import { colors } from 'global/colors';
import { getTimeOfRun, getMaxScore, getScore } from 'functions/reportFunctions';
import { GraphContainer } from './styles';

ChartJS.register(PointElement, LineElement, ChartDataLabels, Title, Tooltip, Legend, TimeScale, TimeSeriesScale);

export default React.memo(
  ({ ticket, inputs, setCurrentInput, selectedInput, showFullOverTime, afterDeadlineInputs, bestOverTimeInput }) => {
    if (showFullOverTime) {
      inputs = [...inputs, ...afterDeadlineInputs];
    }
    if (bestOverTimeInput) inputs.push(bestOverTimeInput);

    // sort inputs by timeOfRequest, otherwise the index might be wrong later on
    inputs = inputs.sort((a, b) => {
      return new Date(a.timeOfRequest) - new Date(b.timeOfRequest);
    });

    let plugins = [];

    const chartRef = useRef(null);
    const MAX_INTERVAL = 20;
    const hasOneInput = inputs?.length === 1;
    let bestRunIndex = -1;
    let selectedRunIndex = -1;
    let finalSubmitIndex = -1;
    let afterDeadlineIndex = -1;
    let maxScore = inputs?.length ? getMaxScore(inputs[0]) : 100;
    let timeLabels = hasOneInput ? [new Date(ticket.startedAt)] : [];
    let scoreLabels = hasOneInput ? [0] : [];
    let timeRunArray = hasOneInput ? ['0:00'] : [];
    let hasExtendedDeadline = ticket.extendedDeadline ?? false;
    let differenceInterval;
    let beforeDeadlineDataset = [];
    let afterDeadlineDataset = [];
    let isLessDataForTimeAxis =
      hasOneInput &&
      new Date(inputs[0]?.timeOfRequest || inputs[0]?.timeOfPaste || inputs[0]?.updatedAt) -
        new Date(ticket.startedAt) <
        30000;

    inputs.forEach((input, index) => {
      timeLabels.push(new Date(input.timeOfRequest || input.timeOfPaste || input.updatedAt));
      scoreLabels.push(getScore(input));
      timeRunArray.push(getTimeOfRun(input, ticket));
      if (input.isBestRun) bestRunIndex = index;
      if (input._id === selectedInput._id) selectedRunIndex = index;
      if (input.isFinalSubmit && !input.isBestAfterDeadline) {
        differenceInterval = Math.max(
          (new Date(input.timeOfRequest || input.timeOfPaste || input.updatedAt) - new Date(ticket.startedAt)) /
            1000 /
            MAX_INTERVAL,
          1.5
        );
        if (!input.afterDeadline) finalSubmitIndex = index;
      }
      if (input.isBestAfterDeadline) afterDeadlineIndex = index;
    });

    // To handle the case when there is only one input present
    bestRunIndex = hasOneInput ? 1 : bestRunIndex;
    selectedRunIndex = hasOneInput ? 1 : selectedRunIndex;
    finalSubmitIndex = hasOneInput ? 1 : finalSubmitIndex;

    let rotateLabels = false;
    // Check if bestRun and afterDeadline dots are close and require rotation
    if (afterDeadlineIndex >= 0 && afterDeadlineIndex - bestRunIndex < 3) {
      if (Math.abs(getScore(inputs[bestRunIndex]) - getScore(inputs[afterDeadlineIndex])) < 3) rotateLabels = true;
    }

    timeLabels.forEach((timeLabel, i) => {
      // If we show the expanded timeline we just need to add the data to one dataset
      // The same is true if the ticket has not been extended
      if (showFullOverTime || !hasExtendedDeadline) {
        beforeDeadlineDataset.push({ x: timeLabel, y: scoreLabels[i] });
        afterDeadlineDataset.push({ x: undefined, y: undefined });
      }
      // If we show the smaller graph and overtime exists, then we need to add the data to two datasets
      // The first dataset will have all the data before the deadline, and the other, the one last overtime dot
      else if (i !== timeLabels.length - 1) {
        beforeDeadlineDataset.push({ x: timeLabel, y: scoreLabels[i] });
        afterDeadlineDataset.push({ x: undefined, y: undefined });
      } else {
        let lastTimeLabel = beforeDeadlineDataset[beforeDeadlineDataset.length - 1];
        let date = new Date(lastTimeLabel.x);
        date.setSeconds(date.getSeconds() + differenceInterval);
        afterDeadlineDataset.push({ x: date, y: scoreLabels[i] });
      }
    });

    const options = {
      responsive: true,
      animation: {
        duration: 0,
      },
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          usePointStyle: true,
          caretPadding: 10,
          filter: (tooltipItem) => (hasOneInput && tooltipItem?.dataIndex === 0 ? false : true),
          callbacks: {
            labelPointStyle: () => ({ pointStyle: 'circle' }),
            labelColor: () => ({ backgroundColor: colors.fsDarkBlue }),
            title: (tooltipItems) => {
              if (!tooltipItems?.length) return;
              let tooltip = `Time: ${timeRunArray[tooltipItems[0].dataIndex]}`;
              if (tooltipItems[0].dataIndex === bestRunIndex) tooltip = `Best Run\n${tooltip}`;
              if (tooltipItems[0].dataIndex === finalSubmitIndex) tooltip = `Final Submit\n${tooltip}`;
              if (tooltipItems[0].dataIndex === afterDeadlineIndex && !showFullOverTime)
                tooltip = `After deadline\n${tooltip}`;
              return tooltip;
            },
            label: (context) => `Score: ${context.dataset.data[context.dataIndex].y}/${maxScore}`,
          },
        },
        datalabels: {
          color: colors.darkGray,
          align: 'top',
          rotation: rotateLabels ? 340 : 0,
          labels: {
            title: {
              font: {
                weight: 'bold',
              },
            },
          },
          display: true,
          formatter: (_, context) => {
            if (context.dataIndex === bestRunIndex) return 'BEST RUN';
            if (context.dataIndex === afterDeadlineIndex && !showFullOverTime) return 'OVERTIME';
            return '';
          },
        },
      },
      scales: {
        x: {
          label: {
            color: colors.fsDarkBlue,
          },
          grid: {
            display: false,
          },
          title: {
            display: true,
            text: 'Time (mm:ss)',
            font: { weight: 'bold' },
          },
          type: 'time', // Set type to 'timeseries' if we want points to be equidistant
          suggestedMin: ticket.startedAt,
          ticks: {
            maxTicksLimit: MAX_INTERVAL,
            // Needs to have the same rotation as the score graph, otherwise it will not align
            maxRotation: 27,
            minRotation: 27,
            font: { size: isMobile ? 8 : 12 },
            callback: (value, a, b) => {
              if (isLessDataForTimeAxis && a !== b.length - 1) return;
              let isOverDeadline =
                new Date(beforeDeadlineDataset[beforeDeadlineDataset.length - 1].x) < new Date(value);
              let time = moment(value) - moment(ticket.startedAt);
              let minutes = Math.floor(time / 60000);
              let seconds = ((time % 60000) / 1000).toFixed(0);

              // If we show the smaller graph, then we dont want to show the overtime tick, so return empty string
              if (!showFullOverTime && isOverDeadline) return '';
              return seconds === 60 ? minutes + 1 + ':00' : minutes + ':' + (seconds < 10 ? '0' : '') + seconds;
            },
          },
        },
        y: {
          suggestedMin: 0,
          suggestedMax: 100,
          grid: {
            display: false,
          },
          title: {
            display: true,
            text: 'Score',
            font: { weight: 'bold', size: 13 },
          },
          ticks: {
            font: { size: isMobile ? 8 : 12 },
          },
        },
      },
      layout: {
        padding: {
          top: 45,
          right: 30,
          bottom: 30,
        },
      },
      elements: {
        point: {
          radius: (ctx) => (hasOneInput && ctx.dataIndex === 0 ? 0 : 5),
          hoverRadius: (ctx) => (hasOneInput && ctx.dataIndex === 0 ? 0 : 8),
          hoverBackgroundColor: colors.darkGray3,
          pointHoverBorderColor: colors.darkGray3,
          hoverCursor: 'pointer',
        },
        line: {
          borderColor: colors.darkGray3,
        },
      },
      onHover: (_, el) => {
        let canvas = document.getElementById('timelineGraph');
        canvas.style.cursor = el[0] ? 'pointer' : 'default';
      },
    };

    const pointColors = scoreLabels.map((_, index) => (index > selectedRunIndex ? colors.gray5 : colors.darkGray3));

    const data = {
      datasets: [
        {
          data: beforeDeadlineDataset,
          pointBackgroundColor: pointColors,
          pointBorderColor: pointColors,
          segment: {
            borderColor: (ctx) => (ctx.p0DataIndex < selectedRunIndex ? colors.darkGray3 : colors.gray5),
          },
        },
      ],
    };

    // If not showing full overtime use the separate dataset showing just one floating "overtime" dot at the end
    if (!showFullOverTime)
      data.datasets.push({
        data: afterDeadlineDataset,
        pointBackgroundColor: pointColors,
        pointBorderColor: pointColors,
        segment: {
          borderColor: (ctx) => (ctx.p0DataIndex < selectedRunIndex ? colors.darkGray3 : colors.gray5),
        },
      });

    const onClick = (event) => {
      const { current: chart } = chartRef;
      if (!chart) return;
      let element = getElementAtEvent(chart, event);
      if (!element.length) return;
      const { index } = element[0];
      if (hasOneInput && index === 0) return;
      setCurrentInput(hasOneInput ? inputs[0] : inputs[index]);
    };

    // If showFullOverTime is true, draw a line at the start of overtime
    if (showFullOverTime) {
      let timeForOverTimeLine = new Date(inputs[finalSubmitIndex].timeOfRequest).getTime();

      plugins.push({
        id: 'OvertimeLine',
        afterDatasetsDraw: ({ ctx, scales }) => {
          let xaxis = scales['x'];
          let yaxis = scales['y'];
          let xValue = xaxis.getPixelForValue(timeForOverTimeLine);

          // if the xValue is less than 600 px, then the text should be on the right side of the line
          const textOnRight = xValue < 600;

          ctx.save();
          ctx.font = '15px';
          ctx.textBaseline = 'bottom';
          ctx.fillStyle = '#000000';
          ctx.fillText('Start of overtime', textOnRight ? xValue + xValue / 50 : xValue - 100, 70);
          ctx.lineWidth = 3;
          ctx.beginPath();
          ctx.moveTo(xValue, yaxis.top);
          ctx.strokeStyle = colors.fsDarkBlue;
          ctx.lineTo(xValue, yaxis.bottom);
          ctx.stroke();
        },
      });
    }

    return (
      <GraphContainer>
        <Line id="timelineGraph" ref={chartRef} options={options} data={data} onClick={onClick} plugins={plugins} />
      </GraphContainer>
    );
  }
);
